import { formatDate } from 'date-fns'
import {
  addOrdinalSuffix,
  isNumeric,
  capitalizeFirstLetter,
} from '@/utils/helpers.js'

export const getParts = (cron) => {
  const parts = {
    minute: '0',
    hour: '0',
    day: '*',
    month: '*',
    weekDay: '*',
  }

  if (cron) {
    const elements = cron.split(' ')

    parts.day = elements[2]
    parts.month = elements[3]
    parts.weekDay = elements[4]
  }

  return parts
}

export const buildList = (date, series) => {
  if (!date) return []
  const shortcuts = [
    {
      id: -1,
      title: 'Does not repeat',
      type: null,
      frequency: null,
      cron: null,
      ends_after_count: null,
      ends_after_date: null,
    },
    {
      id: -2,
      title: 'Daily',
      type: 'Daily',
      frequency: 1,
      cron: '0 0 * * *',
      ends_after_count: null,
      ends_after_date: null,
    },
    {
      id: -3,
      title: 'Weekly on ' + formatDate(new Date(date), 'iii'),
      type: 'Weekly',
      frequency: 1,
      cron: formatDate(new Date(date), '0 0 * * iii'),
      ends_after_count: null,
      ends_after_date: null,
    },
    {
      id: -4,
      title: 'Fortnightly on ' + formatDate(new Date(date), 'iii'),
      type: 'Weekly',
      frequency: 2,
      cron: formatDate(new Date(date), '0 0 * * iii'),
      ends_after_count: null,
      ends_after_date: null,
    },
    {
      id: -5,
      title: 'Monthly on ' + formatDate(new Date(date), 'do'),
      short_title: 'on ' + formatDate(new Date(date), 'do'),
      type: 'Monthly',
      frequency: 1,
      cron: formatDate(new Date(date), '0 0 d * *'),
      ends_after_count: null,
      ends_after_date: null,
    },
    {
      id: -6,
      title:
        'Monthly on the ' +
        getWeekOfMonth(date) +
        ' ' +
        formatDate(new Date(date), 'iii'),
      short_title:
        'on the ' +
        getWeekOfMonth(date) +
        ' ' +
        formatDate(new Date(date), 'iii'),
      type: 'Monthly',
      frequency: 1,
      cron:
        '0 0 * * ' +
        formatDate(new Date(date), 'iii') +
        '#' +
        getWeekOfMonth(date, true),
      ends_after_count: null,
      ends_after_date: null,
    },
    {
      id: -7,
      title: 'Annually on ' + formatDate(new Date(date), 'MMMM, do'),
      type: 'Annually',
      frequency: 1,
      cron: formatDate(new Date(date), '0 0 d MM *'),
      ends_after_count: null,
      ends_after_date: null,
    },
  ]
  if (series) {
    const check = shortcuts.find((x) => {
      return (
        (x.cron === series.cron &&
          x.type === series.type &&
          x.frequency === series.frequency &&
          (x.ends_after_count ?? undefined) ===
            (series.ends_after_count ?? undefined) &&
          (x.ends_after_date ?? undefined) ===
            (series.ends_after_date ?? undefined)) ||
        series.id === x.id
      )
    })

    if (!check) {
      shortcuts.push({
        ...series,
        title: getDescription(series),
        id: -99999,
        is_custom: true,
      })
    } else {
      shortcuts.push({
        id: -99999,
        title: 'HIDDEN',
        is_hidden: true,
      })
    }
  } else {
    shortcuts.push({
      id: -99999,
      title: 'HIDDEN',
      is_hidden: true,
    })
  }
  shortcuts.push({
    title: 'Custom...',
    is_open_settings: true,
  })
  console.log(series)
  console.log(shortcuts)
  return shortcuts
}

export const buildCron = (
  type,
  frequency,
  monthOption,
  month,
  week,
  weekDay,
  day,
) => {
  switch (type) {
    case 'Daily':
      return '0 0 *' + (frequency > 1 ? '/' + frequency : '') + ' * *'
    case 'Weekly':
      return '0 0 * * ' + weekDay.filter((item) => item !== '').join(',')
    case 'Monthly':
      return monthOption?.cron
    case 'Annually':
      return '0 0 ' + day + ' ' + month + ' *'
  }
}

const getWeekOfMonth = (date, justNumber) => {
  var firstWeekday = new Date(date).getDay() - 1
  if (firstWeekday < 0) firstWeekday = 6
  var offsetDate = new Date().getDate() + firstWeekday - 1
  if (justNumber) return Math.floor(offsetDate / 7) + 1
  return ['first', 'second', 'third', 'fourth', 'last'][
    Math.floor(offsetDate / 7)
  ]
}

const getDescription = (series) => {
  const parts = getParts(series.cron)
  let pattern, frequency, days, week, month

  switch (series.type ?? 'Does not repeat') {
    case 'Daily':
      pattern =
        series.frequency === 1 ? 'Daily' : 'Every ' + series.frequency + ' days'
      break
    case 'Weekly':
      frequency =
        series.frequency === 1
          ? 'Weekly'
          : 'Every ' + series.frequency + ' weeks'

      days = parts.weekDay.split(',').map(function (d) {
        return capitalizeFirstLetter(d.toLowerCase())
      })

      pattern = frequency + ' on ' + days.join(' and ')
      break
    case 'Monthly':
      frequency =
        series.frequency === 1
          ? 'Monthly'
          : 'Every ' + series.frequency + ' months'
      if (isNumeric(parts.day)) {
        pattern = frequency + ' on the ' + addOrdinalSuffix(parts.day)
      } else {
        const weekDays = parts.weekDay.split('#')[0]
        const days = weekDays.split(',').map(function (d) {
          return capitalizeFirstLetter(d.toLowerCase())
        })

        week = parts.weekDay.split('#')[1]
        pattern =
          frequency +
          ' on the ' +
          (week === 5
            ? 'Last'
            : addOrdinalSuffix(week) + ' ' + days.join(' and '))
      }

      break
    case 'Annually':
      pattern =
        series.frequency === 1
          ? 'Annually'
          : 'Every ' + series.frequency + ' years'
      month = parts.month.split('#')[0]
      pattern +=
        ' on ' +
        [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ][Number(month)]
      days = parts.day.split('#')[0]
      pattern += ', ' + addOrdinalSuffix(days)
      break
    default:
      return 'Unknown pattern'
  }

  if (series.ends_after_count) {
    return pattern + ' ' + series.ends_after_count + ' times'
  } else if (series.ends_after_date) {
    return (
      pattern +
      ' until ' +
      formatDate(new Date(series.ends_after_date), 'd-MMM-yyyy')
    )
  }

  return pattern
}
